import {
  LogLevel,
  EventType,
  BrowserCacheLocation,
  PublicClientApplication,
  InteractionRequiredAuthError
} from '@azure/msal-browser';
import {
  HOME_PAGE,
  FORMATION_SLUG,
  AUTH_STATUS_NAME,
  CXTIERRA_BASE_URL,
  MSAL_REDIRECT_URI,
  SUCCESS_PASSWORD_RESET_URL
} from '~/utils/enums';

export default defineNuxtPlugin(() => {
  const store = useStore();
  const route = useRoute();
  const router = useRouter();
  const { isEdge, isFirefox, isIE, isSafari } = useDevice();

  let tokenExpirationTimer;

  // MSAL configuration object
  const msalConfig = {
    auth: {
      redirectUri: MSAL_REDIRECT_URI,
      navigateToLoginRequestUrl: true,
      clientId: process.env.APP_MS_CLIENT_ID,
      authority: process.env.APP_MS_AUTHORITY_SIGNIN,
      knownAuthorities: process.env.APP_MS_KNOWN_AUTHORITIES.split(',')
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          const logMethods = {
            [LogLevel.Info]: console.info,
            [LogLevel.Error]: console.error,
            [LogLevel.Warning]: console.warn,
            [LogLevel.Verbose]: console.debug
          };
          (logMethods[level] || console.table)(message);
        },
        logLevel: process.env.APP_IS_PROD_ENV === 'enabled' ? LogLevel.Error : LogLevel.Verbose
      }
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE || isEdge || isFirefox
    }
  };

  // Create MSAL instance
  const msalInstance = new PublicClientApplication(msalConfig);

  // Redirect after handling redirect end event
  const redirectAfterRedirectEnd = () => {
    if (!getIsAuthenticated() || route.path !== REGISTER_BASE_URL) return;

    if (!route.query.token) {
      router.replace(route.query.type === 'cxtierra' ? CXTIERRA_BASE_URL : HOME_PAGE);
    }
  };
  // Redirect URI for successful login
  const redirectAfterLogin = payload => {
    if (!payload) return;
    const redirect = decodeURIComponent(payload.state);
    if (redirect) setTimeout(() => router.replace(redirect), 1000);
  };

  // Initialize MSAL instance
  const initialize = async () => {
    await msalInstance.initialize();

    // Use MSAL event callback to handle events by type
    msalInstance.addEventCallback(async ({ eventType, payload, interactionType }) => {
      if (eventType === EventType.HANDLE_REDIRECT_END) redirectAfterRedirectEnd();
      if (eventType === EventType.LOGIN_SUCCESS) {
        setupTokenExpirationTimer();
        redirectAfterLogin(payload);
      }
      if (eventType === EventType.ACQUIRE_TOKEN_SUCCESS) redirectAfterLogin(payload);
      if (eventType === EventType.ACQUIRE_TOKEN_FAILURE) await refreshAccessToken();
      // Fix for Safari BFCACHE issue
      if (
        eventType === EventType.RESTORE_FROM_BFCACHE &&
        isSafari &&
        interactionType == 'redirect'
      ) {
        window.location.href = HOME_PAGE;
      }
    });

    await msalInstance
      .handleRedirectPromise()
      .then(handleRedirectResponse)
      .catch(async error => {
        const { errorCode, errorMessage } = error;
        if (errorCode === 'access_denied' && errorMessage.includes('AADB2C90118')) {
          await passwordReset();
        }
        setLoadingAuth(false);
      });
  };

  // Set up timer for refreshing access token upon expiration
  const setupTokenExpirationTimer = () => {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length) {
      const account = accounts[0];
      if (account.idTokenClaims?.exp) {
        const tokenExpirationTime = account.idTokenClaims.exp * 1000;
        const timeUntilExpiration = tokenExpirationTime - Date.now();

        clearTimeout(tokenExpirationTimer);

        tokenExpirationTimer = setTimeout(() => {
          refreshAccessToken(account);
        }, timeUntilExpiration);
      }
    }
  };

  // Refresh access token
  const refreshAccessToken = async account => {
    const { idToken } = await msalInstance.acquireTokenSilent({
      account,
      scopes: ['openid', 'offline_access', process.env.APP_MS_EXPOSE_API]
    });

    setupTokenExpirationTimer();

    return idToken;
  };

  // Handle redirect response after login or other flows
  const handleRedirectResponse = async resp => {
    const isInIframe = window.parent !== window;
    if (isInIframe) return;

    if (resp) {
      const { idTokenClaims } = resp;
      const policy = idTokenClaims.acr.toUpperCase();
      if (policy === 'B2C_1A_SIGNIN') {
        await handleResponse(resp);
      } else {
        await handlePolicyChange(policy);
      }
    } else {
      await ssoSilent();
    }
  };

  // Set active account and perform post-login actions
  const setAccount = async account => {
    msalInstance.setActiveAccount(account);
    window.dispatchEvent(new CustomEvent('app-on-login'));

    if (!route.query.token) {
      if (!store.getters['auth/getByField']('user')) await store.dispatch('auth/getUserProfile');
      store.commit('auth/loggedIn', true);
      verifyEnrolledState(AUTH_STATUS_NAME.login);

      if (!store.getters['profile/getByField']('favoriteLoaded')) {
        await store.dispatch('profile/getFavorites');
      }
    }

    setLoadingAuth(false);
  };

  // Verify user's enrollment state for specific formations
  const verifyEnrolledState = hash => {
    const {
      name,
      params: { slug }
    } = route;
    if (FORMATION_SLUG === name && hash === AUTH_STATUS_NAME.login) {
      store.dispatch('profile/checkUserIsEnrolledInEvent', { slug });
    }
  };

  // Select the appropriate account from available accounts
  const selectAccount = async () => {
    const currentAccounts = msalInstance.getAllAccounts();

    if (!currentAccounts.length) return;

    if (currentAccounts.length === 1) {
      await setAccount(currentAccounts[0]);
      return;
    }

    const validAccounts = currentAccounts.filter(
      account =>
        account.homeAccountId.toUpperCase().includes('B2C_1A_SIGNIN') &&
        account.idTokenClaims.iss
          .toUpperCase()
          .includes(process.env.APP_MS_KNOWN_AUTHORITIES.split(',')[0]) &&
        account.idTokenClaims.aud === msalConfig.auth.clientId
    );

    if (validAccounts.length === 1) {
      await setAccount(validAccounts[0]);
    } else if (
      validAccounts.every(account => account.localAccountId === validAccounts[0].localAccountId)
    ) {
      await setAccount(validAccounts[0]);
    } else {
      await signOut(); // Logout all to be safe if multiple users are detected
    }
  };

  // Handle authentication response after login or token acquisition
  const handleResponse = async response => {
    if (response) {
      const { account } = response;
      await setAccount(account);
    } else {
      await selectAccount();
    }
  };

  // Handle policy-specific changes such as profile edit or password reset
  const handlePolicyChange = async policy => {
    if (policy === 'B2C_1A_PROFILEEDIT') {
      console.log('Profile updated successfully. Please log in again.');
    } else if (policy === 'B2C_1A_PASSWORDRESET') {
      await signOut(SUCCESS_PASSWORD_RESET_URL);
    }
  };

  // Login using a redirect flow
  const loginRedirect = async (state, authority, params = {}) => {
    const request = {
      scopes: ['openid', 'offline_access', process.env.APP_MS_EXPOSE_API],
      state: state ? encodeURIComponent(state) : undefined,
      authority,
      loginHint: params.email || ''
    };

    await msalInstance.loginRedirect(request);
  };

  // Redirect with custom parameters based on action type
  const redirectUriCustom = async (action, returnUri, loginParams) => {
    loadingPage(true, action);

    await loginRedirect(
      returnUri,
      action === 'passwordReset' ? process.env.APP_MS_AUTHORITY_PASSWORDRESET : null,
      loginParams
    );
  };

  // Sign in user with optional parameters
  const signIn = async (returnUri, loginParams) =>
    redirectUriCustom('signIn', returnUri, loginParams);

  const ssoFromExternal = async (state, loginParams) => {
    if (getActiveAccount()) {
      if (state) {
        store.commit('auth/redirect', decodeURIComponent(state));
      }
    } else {
      await loginRedirect(state, undefined, loginParams);
    }
  };

  // Reset password flow
  const passwordReset = async (returnUri, loginParams) =>
    redirectUriCustom('passwordReset', returnUri, loginParams);

  // Sign out user and clear session data
  const signOut = async route => {
    loadingPage(true, 'signOut');

    await msalInstance.logoutRedirect({
      account: msalInstance.getActiveAccount(),
      postLogoutRedirectUri: route || undefined
    });
  };

  // Acquire token or refresh access token
  const acquireToken = async () => {
    const currentAccounts = msalInstance.getAllAccounts();

    if (!currentAccounts.length) return null;
    const account = currentAccounts[0];

    if (!account.idToken) return await refreshAccessToken(account);
    return account.idToken;
  };

  // Perform silent SSO
  const ssoSilent = async () => {
    const account = getActiveAccount();
    if (!account) {
      setLoadingAuth(false);
      return null;
    }

    try {
      const response = await msalInstance.ssoSilent({ account });
      await setAccount(response.account);
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.warn('Silent SSO failed, user interaction may be needed');
      } else {
        console.error('Error during silent SSO:', error);
      }
    }
  };

  // Check if user is authenticated
  const getIsAuthenticated = () => {
    const accounts = msalInstance.getAllAccounts();
    return !!accounts?.length;
  };

  // Get the active account
  const getActiveAccount = () => msalInstance.getActiveAccount();

  // Set loading state for authentication process
  const loadingPage = (status, action = '') => {
    store.commit('auth/loading', { status, message: action });
  };

  // Set loading state for auth-related operations
  const setLoadingAuth = state => {
    store.commit('auth/setLoadingAuth', state);
  };

  return {
    provide: {
      msal: {
        signIn,
        signOut,
        ssoSilent,
        initialize,
        acquireToken,
        passwordReset,
        ssoFromExternal,
        getActiveAccount,
        getIsAuthenticated
      }
    }
  };
});
